import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { imageUrl } from '../lib/images';

export default function BilopbevaringPage() {
  return (
    <>
      <SEOHead
        title="Bilopbevaring | Sikret opbevaring af biler | MC Transport"
        description="Sikret bilopbevaring hos MC Transport. 80.000 m² indhegnet plads og 6.000 m² haller med 64 overvågningskameraer og døgnbemandet vagtcentral 24/7."
      />

      {/* Hero */}
      <section className="relative bg-zinc-950 text-white py-16 lg:py-24 border-b border-zinc-800 overflow-hidden">
        <img
          src={imageUrl('/_assets/annexgaardsvej-7-luftfoto-2-306.jpg', 'width=1920,quality=80,format=auto')}
          alt="MC Transport sikret plads til bilopbevaring"
          className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.35]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center sm:text-left">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-600/90 text-white text-xs font-bold uppercase tracking-wider mb-4 border border-red-500/50">
              <i className="fa-solid fa-shield-halved" aria-hidden="true" />
              <span>Sikret Plads · Døgnovervågning</span>
            </div>

            <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white leading-tight">
              Bilopbevaring
            </h1>

            <p className="mt-4 text-base sm:text-lg text-zinc-200 leading-relaxed font-normal">
              Hos <Link to="/" className="font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors">MC Transport</Link> tilbyder vi professionel og sikret bilopbevaring af alle typer køretøjer, herunder personbiler, varevogne, flådebiler, lastbiler samt skadede og havarerede køretøjer. Vores anlæg er indrettet med højeste fokus på sikkerhed, diskretion og nem adgang for transportører.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded shadow transition-colors"
              >
                Få tilbud på bilopbevaring
              </Link>
              <a
                href="tel:+4570700800"
                className="px-6 py-3.5 bg-zinc-900/90 hover:bg-zinc-800 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded border border-white/20 transition-colors"
              >
                Vagtcentral: +45 70 700 800
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Faciliteter & Sikkerhed */}
      <section className="py-16 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
              Sikre rammer om dine køretøjer
            </span>
            <h2 className="font-condensed text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-zinc-950 mt-1">
              Faciliteter til bilopbevaring
            </h2>
            <p className="text-sm sm:text-base text-zinc-600 mt-2">
              Opbevaring tilpasset forsikringsselskaber, leasingselskaber, værksteder, vognmænd og private.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="w-12 h-12 rounded-lg bg-red-600 text-white flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-expand" aria-hidden="true" />
              </div>
              <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-900 mb-2">
                80.000 m² sikret areal
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed">
                Stort indhegnet pladsareal med faste kørebaner, god plads til manøvrering af autotransportere samt 6.000 m² overdækkede haller.
              </p>
            </div>

            <div className="p-6 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="w-12 h-12 rounded-lg bg-red-600 text-white flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-video" aria-hidden="true" />
              </div>
              <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-950 mb-2">
                64 overvågningskameraer
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed">
                Døgnkontinuerlig HD- og infrarød videoovervågning, der dækker alle indkørsler, porte, hegnslinjer og opbevaringsområder.
              </p>
            </div>

            <div className="p-6 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="w-12 h-12 rounded-lg bg-red-600 text-white flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-door-open" aria-hidden="true" />
              </div>
              <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-950 mb-2">
                24/7 adgang & omlæsningssluse
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed">
                Automatisk slusesystem sikrer, at biler kan leveres og afhentes døgnet rundt i samspil med vores <Link to="/kontakt" className="text-red-600 font-semibold hover:underline">døgnbemandede vagtcentral</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tillægsydelser ved bilopbevaring (Struktur forberedt til kundens specifikke ydelser) */}
      <section className="py-16 bg-zinc-100 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
              Fleksible løsninger
            </span>
            <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-zinc-950 mt-1">
              Tillægsydelser ved bilopbevaring
            </h2>
            <p className="text-sm text-zinc-600 mt-2">
              Vi tilbyder en række tillægsydelser i forbindelse med opbevaring af køretøjer. Nedenfor ses oversigten over tilgængelige services for både kort- og langtidsopbevaring.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Tillægsydelse boks 1 */}
            <div className="bg-white p-6 rounded-xl border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded bg-red-100 text-red-600 flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-truck-ramp-box" aria-hidden="true" />
                </div>
                <h3 className="font-condensed text-xl font-bold uppercase text-zinc-900 mb-2">
                  Kombineret transport & opbevaring
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Vi kan hente dine køretøjer direkte med vores <Link to="/ydelser/autotransport-ydelser" className="text-red-600 hover:underline font-medium">autotransportere</Link> og levere dem videre på aftalt tidspunkt.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500 font-medium">
                Indgår som del af vores transportservice
              </div>
            </div>

            {/* Tillægsydelse boks 2 */}
            <div className="bg-white p-6 rounded-xl border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded bg-red-100 text-red-600 flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-warehouse" aria-hidden="true" />
                </div>
                <h3 className="font-condensed text-xl font-bold uppercase text-zinc-900 mb-2">
                  Indendørs eller udendørs plads
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Mulighed for placering på udendørs sikret plads eller i 6.000 m² overdækkede lagerhaller efter opgavens karakter.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500 font-medium">
                Tilpasses efter behov og varighed
              </div>
            </div>

            {/* Tillægsydelse boks 3 */}
            <div className="bg-white p-6 rounded-xl border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded bg-red-100 text-red-600 flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-clipboard-check" aria-hidden="true" />
                </div>
                <h3 className="font-condensed text-xl font-bold uppercase text-zinc-900 mb-2">
                  Håndtering af skadede biler
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Specialplads indrettet til modtagelse af kollissskadede eller ukørende biler via spil, kranbil eller skydeladsbiler.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500 font-medium">
                Til forsikring, taksator og værksted
              </div>
            </div>

            {/* Tillægsydelse boks 4 (Forberedt til kundens specifikke tillægsydelser) */}
            <div className="bg-white p-6 rounded-xl border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded bg-red-100 text-red-600 flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-battery-half" aria-hidden="true" />
                </div>
                <h3 className="font-condensed text-xl font-bold uppercase text-zinc-900 mb-2">
                  Tilsyn og vedligehold
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Struktur forberedt til individuelle aftaler om batterivedligehold, dæktrykskontrol og klargøring under opbevaring.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500 font-medium">
                Aftales individuelt
              </div>
            </div>

            {/* Tillægsydelse boks 5 */}
            <div className="bg-white p-6 rounded-xl border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded bg-red-100 text-red-600 flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-car-burst" aria-hidden="true" />
                </div>
                <h3 className="font-condensed text-xl font-bold uppercase text-zinc-900 mb-2">
                  Taksator- og besigtigelsesadgang
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Sikker adgang for forsikringstaksatorer og samarbejdspartnere til vurdering af skadede køretøjer.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500 font-medium">
                Døgnadgang via sluse
              </div>
            </div>

            {/* Tillægsydelse boks 6 */}
            <div className="bg-white p-6 rounded-xl border border-zinc-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded bg-red-100 text-red-600 flex items-center justify-center text-lg mb-3">
                  <i className="fa-solid fa-circle-question" aria-hidden="true" />
                </div>
                <h3 className="font-condensed text-xl font-bold uppercase text-zinc-900 mb-2">
                  Særlige kundeønsker
                </h3>
                <p className="text-xs text-zinc-600 leading-relaxed">
                  Har du særlige krav til håndtering, dækservice eller mærkning af flådebiler, sammensætter vi en tilpasset aftale.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500 font-medium">
                Kontakt vagtcentralen for detaljer
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-xs sm:text-sm">
            <i className="fa-solid fa-circle-info mr-2 text-amber-600" aria-hidden="true" />
            <span>
              Bemærk: Den endelige liste over specifikke tillægsydelser og pakkepriser kan tilpasses direkte efter aftale. Kontakt os på telefon <strong>70 700 800</strong> eller benyt formularen for en konkret forespørgsel.
            </span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs uppercase font-bold tracking-widest text-red-500 font-condensed">
            Døgnbemandet vagtcentral 24/7
          </span>
          <h2 className="font-condensed text-3xl sm:text-5xl font-extrabold uppercase tracking-tight mt-2">
            Hør mere om bilopbevaring
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-2xl mx-auto">
            Vores vagtcentral sidder klar døgnet rundt til at besvare spørgsmål og koordinere både akut afhentning og planlagt opbevaring.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="px-8 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase rounded shadow transition-colors"
            >
              Gå til kontaktformular
            </Link>
            <a
              href="tel:+4570700800"
              className="px-8 py-3.5 bg-zinc-800 hover:bg-zinc-700 text-white font-condensed text-xl font-bold uppercase rounded border border-white/20 transition-colors"
            >
              Ring 70 700 800
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
